import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

export interface StatusOption {
  value: string;
  label: string;
  background?: string;
}

// Unified status pill used across list tables. Two modes:
//  - read-only chip (no `onChange`): a colored rounded label.
//  - editable select-pill (`onChange` provided): the colored dropdown lifted
//    from the publications visibility column.
// Both are driven by an `options` list carrying `{ value, label, background }`.
interface Props {
  value: string;
  options: StatusOption[];
  onChange?: (val: string) => void;
  width?: number | string;
  /** Values to hide from the dropdown (still used for display/color). */
  excludeFromMenu?: string[];
}

const DEFAULT_BG = "#747474";

function StatusChip({
  value,
  options,
  onChange,
  width = 140,
  excludeFromMenu = [],
}: Props): JSX.Element {
  const selected = options.find((o) => o.value === value);
  const bg = selected?.background || DEFAULT_BG;
  const label = selected?.label || value || "-";

  if (!onChange) {
    return (
      <MDBox
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        px={1.5}
        sx={{
          background: bg,
          color: "#fff",
          borderRadius: "21px",
          height: "28px",
          minWidth: "88px",
          fontSize: "12px",
          fontWeight: 400,
          textTransform: "uppercase",
        }}
      >
        <MDTypography sx={{ color: "#fff", fontSize: "12px", fontWeight: 400 }}>
          {label}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <FormControl sx={{ width }} size="medium">
      <Select
        displayEmpty
        value={value}
        sx={{
          boxShadow: "none",
          textAlign: "center",
          borderRadius: "21px",
          height: "35px",
          color: "#fff",
          background: bg,
          textTransform: "uppercase",
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "transparent" },
          "& .MuiOutlinedInput-input": {
            padding: "10px",
            color: "#fff",
            fontSize: "12px",
            fontWeight: "400",
            borderRadius: "21px",
            border: "none",
            boxShadow: "none",
            "&.Mui-focused, &.Mui-focusVisible": { outline: "none", boxShadow: "none" },
          },
        }}
        renderValue={() => (value ? label : <em>Select an option</em>)}
        onChange={(e) => onChange(e.target.value as string)}
      >
        {options
          .filter((o) => o.value !== value && !excludeFromMenu.includes(o.value))
          .map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
}

export default StatusChip;
